
import { useCallback, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError, UploadProgress } from '../../api/client';
import { placementApi } from '../../api/endpoints';
import type {
  Id,
  PlacementProposalRequest,
  PlacementVideoRun,
  PlacementVideoRunRequest,
  PlacementVideoSource,
  ToolTask,
} from '../../types/api';
import { MAX_PLACEMENT_VIDEO_BYTES, isTaskFinished } from '../../types/api';
import { pollIntervalFrom } from '../../hooks';
import { parseProposal, type PlacementProposalView } from './types';

export const placementKeys = {
  root: ['placement'] as const,
  proposal: (proposalId: string) => ['placement', 'proposal', proposalId] as const,
  videoRun: (runId: string) => ['placement', 'video-run', runId] as const,
};

export function useCreateProposal() {
  return useMutation<ToolTask, ApiError, PlacementProposalRequest>({
    mutationFn: (payload) => placementApi.createProposal(payload),
    retry: false,
  });
}

export function useProposal(
  proposalId: string | undefined,
): UseQueryResult<PlacementProposalView, Error> {
  return useQuery<PlacementProposalView, Error>({
    queryKey: placementKeys.proposal(proposalId ?? ''),
    queryFn: async () => {
      const raw = await placementApi.get(proposalId as Id);
      const parsed = parseProposal(raw);
      if (!parsed) {
        throw new Error('Предложение не читается. Запустите подбор заново.');
      }
      return parsed;
    },
    enabled: Boolean(proposalId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const status = (error as ApiError).status;
      return status !== 404 && failureCount < 2;
    },
  });
}

export interface PlacementProgress {
  stage: string | null;
  percent: number | null;
}

export function placementProgress(task: ToolTask | undefined): PlacementProgress {
  const progress = task?.progress ?? {};
  const stage = typeof progress.stage === 'string' && progress.stage ? progress.stage : null;
  const raw = Number(progress.pct);
  const percent = Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw * 100))) : null;
  return { stage, percent };
}

export const PLACEMENT_STAGES: readonly string[] = [
  'токенизация',
  'разметка якорей',
  'правила плотности',
  'подбор медиа',
  'готово',
];

export const PLACEMENT_VIDEO_STAGES: readonly string[] = [
  'скачивание исходника',
  'тайминги речи',
  'аудиодорожка',
  'разметка и подбор',
  'скачивание медиа',
  'сборка манифеста',
  'проверка манифеста',
  'сборка ролика',
  'выгрузка',
  'готово',
];

const NESTED_PLACEMENT_STAGE = 'разметка и подбор';

export function videoStageIndex(stage: string | null): number {
  if (!stage) return -1;
  const direct = PLACEMENT_VIDEO_STAGES.indexOf(stage);
  if (direct >= 0) return direct;
  if (PLACEMENT_STAGES.includes(stage)) {
    return PLACEMENT_VIDEO_STAGES.indexOf(NESTED_PLACEMENT_STAGE);
  }
  return -1;
}

export interface PlacementVideoUpload {
  upload: UseMutationResult<PlacementVideoSource, ApiError, File>;
  progress: number | null;
  reset: () => void;
}

export function useUploadPlacementVideo(): PlacementVideoUpload {
  const [progress, setProgress] = useState<number | null>(null);
  const upload = useMutation<PlacementVideoSource, ApiError, File>({
    mutationFn: (file) =>
      placementApi.uploadVideo(file, (event: UploadProgress) => setProgress(event.fraction)),
    retry: false,
    onMutate: () => setProgress(0),
    onSettled: () => setProgress(null),
  });
  const reset = useCallback(() => {
    upload.reset();
    setProgress(null);
  }, [upload]);
  return { upload, progress, reset };
}

export function validatePlacementVideo(file: File | null): string[] {
  if (!file) return ['Файл не выбран'];
  const problems: string[] = [];
  if (file.size === 0) problems.push('Файл пустой');
  if (file.size > MAX_PLACEMENT_VIDEO_BYTES) {
    problems.push(`Файл больше ${Math.round(MAX_PLACEMENT_VIDEO_BYTES / 1024 ** 3)} ГБ`);
  }
  const looksLikeVideo =
    file.type.startsWith('video/') || /\.(mp4|mov|m4v|mkv|webm|avi|mpe?g)$/i.test(file.name);
  if (!looksLikeVideo) problems.push('Нужен видеофайл: mp4, mov, mkv или webm');
  return problems;
}

export function useCreateVideoRun(): UseMutationResult<
  PlacementVideoRun,
  ApiError,
  PlacementVideoRunRequest
> {
  const qc = useQueryClient();
  return useMutation<PlacementVideoRun, ApiError, PlacementVideoRunRequest>({
    mutationFn: (payload) => placementApi.createVideoRun(payload),
    retry: false,
    onSuccess: (run) => {
      qc.setQueryData(placementKeys.videoRun(run.run_id), run);
    },
  });
}

export function isVideoRunActive(run: PlacementVideoRun | undefined): boolean {
  return Boolean(run) && !isTaskFinished(run?.status);
}

export function usePlacementVideoRun(
  runId: string | undefined,
): UseQueryResult<PlacementVideoRun, ApiError> {
  return useQuery<PlacementVideoRun, ApiError>({
    queryKey: placementKeys.videoRun(runId ?? ''),
    queryFn: () => placementApi.videoRun(runId as Id),
    enabled: Boolean(runId),
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return isVideoRunActive(query.state.data) ? pollIntervalFrom(undefined) : false;
    },
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });
}

export interface AudioTimingsUpload {
  start: UseMutationResult<ToolTask, ApiError, { file: File; script: string; language: string }>;
  progress: number | null;
  reset: () => void;
}

export function useAudioTimings(): AudioTimingsUpload {
  const [progress, setProgress] = useState<number | null>(null);
  const start = useMutation<ToolTask, ApiError, { file: File; script: string; language: string }>({
    mutationFn: ({ file, script, language }) =>
      placementApi.audioTimings(file, script, language, (event: UploadProgress) =>
        setProgress(event.fraction),
      ),
    retry: false,
    onMutate: () => setProgress(0),
    onSettled: () => setProgress(null),
  });
  const reset = useCallback(() => {
    start.reset();
    setProgress(null);
  }, [start]);
  return { start, progress, reset };
}

export function validatePlacementAudio(file: File | null): string[] {
  if (!file) return ['Файл не выбран'];
  const problems: string[] = [];
  if (file.size === 0) problems.push('Файл пустой');
  if (file.size > MAX_PLACEMENT_VIDEO_BYTES) {
    problems.push(`Файл больше ${Math.round(MAX_PLACEMENT_VIDEO_BYTES / 1024 ** 3)} ГБ`);
  }
  const looksLikeMedia =
    file.type.startsWith('audio/') ||
    file.type.startsWith('video/') ||
    /\.(mp3|wav|m4a|aac|ogg|opus|flac|mp4|mov|m4v|mkv|webm)$/i.test(file.name);
  if (!looksLikeMedia) problems.push('Нужен аудиофайл: mp3, wav, m4a или ogg');
  return problems;
}
